import Model, { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';

type Highlight = {
  [key: string]: string[];
};

// Index statistics response
export interface IndexStats {
  index: string;
  docCount: number;
  size: string;
  sizeInBytes: number;
  status: 'healthy' | 'warning' | 'critical' | 'unknown';
  rawStats: {
    health: Record<string, unknown>;
    stats: Record<string, unknown>;
  };
}

type Employee = {
  companyId: string;
  companyName: string;
  firstName: string;
  lastName: string;
  payrollId: string;
  email: string;
  tasParticipantState: string;
  participationStatus: string;
};

export type TasProgramInstance = {
  id: string;
  companyId: string;
  companyName: string;
  employeeId: number;
  employeeNameKeyword: string;
  employeePayrollIdKeyword: string;
  personId: number;
  employmentEndDate: string | null;
  employmentTerminationVerified: boolean;
  tasProgramInstanceAttentionPriorityDate?: string;
  tasProgramInstanceIdKeyword?: string;
  tasProgramInstanceCreatedAtDate?: string;
  tasProgramInstanceStateKeyword?: string;
  tasProgramTemplateIdKeyword?: string;
  tasProgramTemplateTypeKeyword?: string;
  tasProgramInstanceLatestSubmissionTs?: string;
  tasProgramInstanceLatestStateChangeAtDate?: string;
  tasProgramInstanceLatestStateChangePersonText?: string;
  tasApplicationLatestSubmissionTs?: string;

  // NEW STANDARDIZED PROGRAM INSTANCE FIELDS (Phase 3 - from TasField with _field_ indicator)
  tasProgramInstanceFieldEmployeeIdInteger?: number;
  tasProgramInstanceFieldEmployeeIdKeyword?: string;
  tasProgramInstanceFieldEmployeeEmailKeyword?: string;
  tasProgramInstanceFieldEstimatedProgramBeginDate?: string;
  tasProgramInstanceFieldEstimatedProgramCompletionDate?: string;
  tasProgramInstanceFieldFtPtOtherKeyword?: string;
  tasProgramInstanceFieldNameKeyword?: string;
  tasProgramInstanceFieldProgramSpecializationKeyword?: string;
  tasProgramInstanceFieldSchoolInstitutionNameKeyword?: string;
  tasProgramInstanceFieldEmployeeTitleKeyword?: string;
  tasProgramInstanceFieldDapipInstitutionIdInteger?: number;
  tasProgramInstanceFieldHireDateDate?: string;

  // Program template fields with keyword variants
  tasProgramTemplateFieldProgramNameKeyword?: string;
  tasProgramTemplateFieldProgramSpecializationKeyword?: string | null;
  tasProgramTemplateFieldVoucherProgramBoolean?: boolean;

  // NEW STANDARDIZED APPLICATION FIELDS (Phase 2)
  recordType?: string; // 'application' or 'instance'
  tasApplicationIdKeyword?: string;
  tasApplicationStateKeyword?: string;
  tasApplicationCreatedAtDate?: string;
  tasApplicationAttentionPriorityDate?: string;
  tasApplicationLatestStateChangePersonText?: string;
  tasApplicationLatestStateChangeAtDate?: string;
  tasApplicationCurrentReviewersKeyword?: number[];
  tasApplicationPossibleReviewersKeyword?: number[];
  tasApplicationCourseCashReservedAmountInteger?: number;
  tasApplicationCourseCashUsedAmountInteger?: number;

  // NEW STANDARDIZED APPROVAL/WORKFLOW FIELDS (Phase 4 - calculated fields)
  programApprovalRequired?: boolean;
  courseApprovalRequired?: boolean;
  courseGradeRequired?: boolean;
  evidenceApprovalRequired?: boolean;
  approverCanEditApplication?: boolean;
  applicationInvoiceDate?: string | null;

  // Program instance approval tracking
  programInstanceAffirmedReviewers?: unknown[];
  programInstanceCurrentReviewers?: number[];
  programInstancePossibleReviewers?: number[];
  tasProgramInstanceApprovalCountSubmissions?: number | null;
  tasProgramInstanceApprovalFirstSubmissionTs?: string | null;
  tasProgramInstanceApprovalCurrentApprovalRole?: string | null;
  tasProgramInstanceCompletionCountSubmissions?: number | null;
  tasProgramInstanceCompletionFirstSubmissionTs?: string | null;
  tasProgramInstanceCompletionCurrentApprovalRole?: string | null;
  tasProgramInstanceWithdrawalCountSubmissions?: number | null;
  tasProgramInstanceWithdrawalFirstSubmissionTs?: string | null;
  tasProgramInstanceWithdrawalCurrentApprovalRole?: string | null;

  // Application approval tracking
  tasApplicationCoursesCountSubmissions?: number | null;
  tasApplicationCoursesFirstSubmissionTs?: string | null;
  tasApplicationCoursesCurrentApprovalRole?: string | null;
  tasApplicationEvidenceCountSubmissions?: number | null;
  tasApplicationEvidenceFirstSubmissionTs?: string | null;
  tasApplicationEvidenceCurrentApprovalRole?: string | null;
  tasApplicationFieldCoursesEndDateDate?: string;
  tasApplicationFieldCoursesBeginDateDate?: string;
  tasApplicationFieldApplicationApproveCoursesWorkflowKeyword?: string;
  tasApplicationFieldApplicationApproveEvidenceWorkflowKeyword?: string;

  // Phase 7: Flattened custom fields for each fieldable type
  // Using OpenSearch flattened datatype to prevent mapping explosion
  tasProgramTemplateCustomFields?: Record<string, unknown>;
  tasProgramInstanceCustomFields?: Record<string, unknown>;
  tasApplicationCustomFields?: Record<string, unknown>;
  tasCourseCustomFields?: Record<string, unknown>;

  // Withdrawal approval tracking
  withdrawalApproval?: {
    affirmed: unknown[];
    approved: boolean;
    required: unknown[];
    currentReviewers: unknown[];
    possibleReviewers: unknown[];
  };

  // Payment tracking fields
  paymentLastDate?: string | null;
  paymentPreApprovedTotal?: number;
  paymentFinalApprovedTotalAmount?: number;
  paymentPendingAmount?: number;
  paymentPaidAmount?: number;
  paymentRefundRequestedAmount?: number;
  paymentRefundPaidBackAmount?: number;
  paymentRefundForgivenAmount?: number;
  paymentRefundCancelledAmount?: number;
  paymentRefundLimitAmount?: number;

  // NEW STANDARDIZED COURSE FIELDS (Phase 6 - from TasField with _field_ indicator)
  courses?: {
    courseId: string;
    tasCourseFieldCourseNameKeyword?: string;
    tasCourseFieldCourseNumberKeyword?: string;
    tasCourseFieldCourseNumberInteger?: number;
    tasCourseFieldCourseDescriptionKeyword?: string;
    tasCourseFieldCourseTuitionInteger?: number;
    tasCourseFieldCourseBooksInteger?: number;
    tasCourseFieldCourseFeesInteger?: number;
    tasCourseFieldCourseGrade?: string;
    tasCourseFieldCourseCreditFloat?: number;
    tasCourseFieldApprovedCourseTotalInteger?: number;
    tasCourseCustomFields?: Record<string, unknown>;
  }[];

  // OpenSearch metadata
  updatedAt?: string;
  indexedAt?: string;
  documentCreatedAt?: string;

  // TasAsset document fields (record_type === 'asset')
  tasAssetIdKeyword?: string;
  tasAssetFilenameKeyword?: string;
  tasAssetNameKeyword?: string;
  tasAssetClassificationKeyword?: string;
  tasAssetDescriptionKeyword?: string;
  tasAssetCreatedAtDate?: string;
  tasAssetUpdatedAtDate?: string;
  attentionPriorityDate?: string;

  // Root-level approvable reviewer fields (TasAsset documents)
  tasApprovableCurrentReviewersKeyword?: number[];
  tasApprovablePossibleReviewersKeyword?: number[];

  // Approval queue state fields (TasAsset documents)
  tasAssetHasPendingApprovablesBoolean?: boolean;
  tasApprovableStatesKeyword?: string[];
};

export interface ApplicationStateCount {
  key: string;
  count: number;
}

export interface AggregatedProgramInstance {
  employeeId: string;
  employeeNameKeyword: string;
  employeePayrollIdKeyword: string;
  programInstanceId: string;
  tasProgramInstanceIdKeyword: string; // Canonical field name for column mapping
  tasProgramInstanceCreatedAtDate: string;
  tasProgramTemplateFieldProgramNameKeyword: string; // ✅ CORRECTED: This field contains program template name (e.g., "MBA", "Bachelor's Degree")
  tasProgramInstanceFieldNameKeyword?: string; // DEPRECATED: Alias for backward compatibility with old column selections
  tasProgramInstanceFieldSchoolInstitutionNameKeyword: string;
  applicationStateCounts: Record<string, number>;
  totalApplications: number;
}

export type Person = {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;

  employees: Employee[];
  isActive: boolean;
  isEnrolled: boolean;
  userNames: string[];

  jurisdictions: string[];
  highlight: Highlight;
};

export type PslfForm = {
  borrowerEmail: string;
  borrowerName: string;
  borrowerSignDate: string;
  borrowerEmployerEmail: string;
  companyId: string;
  documentId: string;
  employerClosed: string;
  employerEmail: string;
  employerName: string;
  employerSignDate: string;
  formAwaitingSignature: boolean;
  formFaxVerified: boolean;
  formId: string;
  formPreApproved: boolean;
  formPrintAndMail: boolean;
  formStatus: string;
  formNonPartner: boolean;
  formType: string;
  payrollId: string;
  profileId: string;
  createdAt: string;
  updatedAt: string;
};

export type TasUnifiedInstitution = {
  identifier: string;
  dapipId: number;
  institutionId: string | null;
  locationName: string;
  locationType: string;
  address: string;
  instIsFeatured: boolean;
  instIsPreferred: boolean;
  lpn: boolean;
};

export type CompanySearchResult = {
  companyIdKeyword: string;
  companyNameText: string;
  companyNameKeyword: string;
  companyTypeKeyword: string;
  companyIsActiveBoolean: boolean;
  companySubDomainKeyword: string;
  companyProductsEnabledKeyword: string[];
  companyProgramTitleText?: string;
  companyIsTaEnabledBoolean?: boolean;
  companyIsTaOnlyBoolean?: boolean;
  companyIsLiveBoolean?: boolean;
  companyHasSsoBoolean?: boolean;
  companyLaunchDateDate?: string;
  companyCreatedAtDate?: string;
  companyPartnerTypeKeyword?: string;
  companyFeaturesKeyword?: string[];
  companyAdminPersonNameText?: string;
  companyAdminUserEmailsText?: string[];
  companyPartnerSuccessAccountManagerNameText?: string;
};

export type ResultItems =
  | Person
  | PslfForm
  | TasProgramInstance
  | TasUnifiedInstitution
  | CompanySearchResult;

// Raw OpenSearch response structure
export interface OpenSearchHit<T = unknown> {
  _index: string;
  _id: string;
  _score: number;
  _routing?: string;
  _source: T;
  highlight?: Record<string, string[]>;
}

export interface OpenSearchHits<T = unknown> {
  total: {
    value: number;
    relation: string;
  };
  maxScore: number;
  hits: OpenSearchHit<T>[];
}

// Aggregation bucket structures
export interface ApplicationStateBucket {
  key: string;
  docCount: number;
}

export interface SampleDocumentHits<T = unknown> {
  total: {
    value: number;
    relation: string;
  };
  maxScore: number;
  hits: OpenSearchHit<T>[];
}

export interface EmployeeBucket {
  key: string;
  docCount: number;
  applicationStates: {
    docCountErrorUpperBound: number;
    sumOtherDocCount: number;
    buckets: ApplicationStateBucket[];
  };
  sampleDocument: {
    hits: SampleDocumentHits<TasProgramInstance>;
  };
}

export interface EmployeesAggregation {
  docCountErrorUpperBound: number;
  sumOtherDocCount: number;
  buckets: EmployeeBucket[];
}

export interface OpenSearchResponse<T = unknown> {
  hits: OpenSearchHits<T>;
  aggregations?: {
    employees?: EmployeesAggregation;
  };
  took: number;
  timedOut?: boolean;
  _shards?: Record<string, unknown>;
}

export default class SearchModel extends Model {
  @attr declare items: unknown[];
  @attr declare total: number;
  @attr declare took: number;

  [Type] = 'search' as const;
}

export interface SearchModelWith<T> {
  items: T[];
  total: number;
  took: number;
  isLoading?: boolean;
}

// Type for raw OpenSearch responses
export type RawSearchResponse<T> = OpenSearchResponse<T>;

// Union type for results that can be either individual or aggregated
export type ProgramsSearchResult =
  | SearchModelWith<TasProgramInstance> // Individual mode
  | SearchModelWith<AggregatedProgramInstance> // Aggregated mode
  | SearchModelWith<Record<string, unknown>>; // Generic dataset mode

export interface SearchFieldCapabilities {
  fullText?: boolean;
  filter?: boolean;
  sort?: boolean;
  aggregate?: boolean;
  range?: boolean;
  nested?: boolean;
  object?: boolean;
}

export interface SearchFieldUiHints {
  filterWidget?: string;
  multiSelect?: boolean;
}

export interface SearchFieldPayload {
  name: string;
  label?: string;
  type?: string;
  sourceField?: string;
  capabilities?: SearchFieldCapabilities;
  uiHints?: SearchFieldUiHints;
  semanticRole?: string;
  semanticRoleLabel?: string;
  reportDatatype?: string;
  isCustom?: boolean;
  enumValues?: string[];
  provenance?: string;
}

export interface SearchFieldsResponse {
  fields?: SearchFieldPayload[];
}

export interface KeyLabelMapsResponse {
  companyEfKeyLabelMap?: Record<string, string>;
  tasTemplateKeyLabelMap?: Record<string, string>;
  tasTemplateKeyProvinceMap?: Record<string, string>;
}

export interface OpenSearchMapping {
  [indexName: string]: {
    mappings: {
      properties: {
        [fieldName: string]: {
          type: string;
          fields?: Record<string, { type: string }>;
          properties?: Record<string, unknown>;
        };
      };
    };
  };
}

export interface SearchMappingWithLabelMapsResponse {
  mapping?: OpenSearchMapping;
  labelMaps?: KeyLabelMapsResponse;
}

export interface SearchAdapter {
  search(query: Record<string, unknown>): Promise<OpenSearchResponse<unknown>>;
  downloadCsv(query: Record<string, unknown>): Promise<void>;
  fields?(
    modelType: string,
    companyId?: string,
    programTemplateId?: string
  ): Promise<SearchFieldsResponse>;
  mappingWithLabelMaps?(
    modelType: string,
    companyId?: string,
    programTemplateId?: string
  ): Promise<SearchMappingWithLabelMapsResponse>;
  mapping?(modelType: string): Promise<OpenSearchMapping>;
}
